// Which sign-in this tab's private work belongs to.
//
// A token refresh is the same session with a new access token. A sign-in, a sign-out, the server
// ending the session, and another sign-in taking over the browser are not. Work that belongs to one
// session -- an activity report, a recovery draft -- captures the scope it started under and checks
// it is still current before it sends and before it publishes, so nothing from one session goes out
// with a later session's token or lands in its state.
//
// The scope has two halves:
// - `generation` is this tab's own counter. It moves on every change of ownership, so a captured
//   scope can tell it is stale, but it means nothing to another tab.
// - `nonce` names one browser sign-in and is shared by every tab through `localStorage`. A sign-in
//   in any tab writes a new one, which tells the other tabs their scope is stale even though their
//   own token still looks fine. Their next refresh proves who the cookie now belongs to before they
//   bind to it again (`identity_refresh_token_submit.ts`).
//
// Like `logout_pending`, this module imports nothing from the `lib` barrel, so `create_store` and the
// auth sagas can both reach it without widening the existing `lib` <-> `lib/auth` cycle. The nonce
// is not a secret and not a security control: same-origin script can rewrite it.
import { v4 } from "uuid";

export const SESSION_SCOPE_STORAGE_KEY = "labfront_session_scope";

export interface SessionScope {
  generation: number;
  nonce: string;
}

// `unbound`: the tab still holds a token, but another tab's sign-in or sign-out made its scope stale
export type SessionScopeStatus = "bound" | "unbound" | "none";

let generation = 0;
let current: SessionScope | null = null;
let unbound = false;
// sign-ins and endings in this tab, so a request can tell which one happened while it was out
let signIns = 0;
let endings = 0;

export const currentSessionScope = (): SessionScope | null => current;

export interface SessionOwnershipMark {
  signIns: number;
  endings: number;
}

export const markSessionOwnership = (): SessionOwnershipMark => ({ signIns, endings });

// What this tab did to its session since `mark`. A later sign-in outranks an ending before it, since
// the tab is signed in again. Another tab's sign-in or sign-out is not counted: that is for the
// identity check to settle.
export const sessionOwnershipSince = (
  mark: SessionOwnershipMark,
): "unchanged" | "signedIn" | "ended" => {
  if (signIns !== mark.signIns) return "signedIn";
  if (endings !== mark.endings) return "ended";
  return "unchanged";
};

export const sessionScopeStatus = (): SessionScopeStatus => {
  if (current) return "bound";
  return unbound ? "unbound" : "none";
};

export const isSessionScopeCurrent = (scope: SessionScope | null | undefined): boolean =>
  current !== null && current.generation === scope?.generation;

// `undefined` when storage cannot be read at all (private mode): the tab then goes by its own scope
export const readSharedSessionNonce = (): string | null | undefined => {
  try {
    return window.localStorage.getItem(SESSION_SCOPE_STORAGE_KEY);
  } catch {
    return undefined;
  }
};

const writeSharedSessionNonce = (nonce: string): void => {
  try {
    window.localStorage.setItem(SESSION_SCOPE_STORAGE_KEY, nonce);
  } catch {
    // private-mode storage throws on write; each tab then keeps its own scope
  }
};

// Hearing a scope end. `signingOut` is the user (or the idle clock) signing out of this tab: the one
// time private work may make a last attempt with the old session's token. Every other end -- the
// server ending the session, another tab's sign-in or sign-out, a new sign-in here -- leaves nothing
// to send it with.
export type SessionScopeEndListener = (ended: SessionScope, cause: { signingOut: boolean }) => void;

const endListeners = new Set<SessionScopeEndListener>();

export const onSessionScopeEnd = (listener: SessionScopeEndListener): (() => void) => {
  endListeners.add(listener);
  return () => {
    endListeners.delete(listener);
  };
};

// Called after the scope stopped being current, so work the listeners start cannot run under it.
const announceEnd = (ended: SessionScope | null, signingOut: boolean) => {
  if (!ended) return;
  endListeners.forEach((listener) => {
    try {
      listener(ended, { signingOut });
    } catch (error) {
      // one listener failing must not keep the others, or the sign-out, from going ahead
      console.error("a session scope end listener failed", error);
    }
  });
};

const enter = (nonce: string): SessionScope => {
  const replaced = current;
  generation += 1;
  current = { generation, nonce };
  unbound = false;
  announceEnd(replaced, false);
  return current;
};

const enterNewBrowserSession = (): SessionScope => {
  const scope = enter(v4());
  writeSharedSessionNonce(scope.nonce);
  return scope;
};

// A confirmed sign-in: a new browser session, whoever it belongs to
export const startSessionScope = (): SessionScope => {
  signIns += 1;
  return enterNewBrowserSession();
};

// The tab joins a browser session it has proved is its own. Binding to the nonce it already holds
// keeps the scope, which is what makes a token refresh not a new session.
export const bindSessionScope = (nonce: string): SessionScope => {
  if (current?.nonce === nonce) return current;
  return enter(nonce);
};

// After a refresh has proved the identity: joins the browser session on record, or records one when
// there is none (the first tab after this shipped, or after storage was cleared)
export const adoptBrowserSessionScope = (): SessionScope => {
  const shared = readSharedSessionNonce();
  if (shared) return bindSessionScope(shared);
  if (shared === undefined) return current ?? enter(v4());
  return enterNewBrowserSession();
};

// The session is over in this tab: signed out, or ended by the server. Called before anything
// asynchronous, so work captured under the old scope sees it at its next check. The shared nonce stays
// until the sign-out is answered (`releaseSharedSessionNonce`): other tabs that hear it go only
// after the server has.
export const endSessionScope = ({
  signingOut = false,
}: { signingOut?: boolean } = {}): SessionScope | null => {
  const ended = current;
  endings += 1;
  generation += 1;
  current = null;
  unbound = false;
  announceEnd(ended, signingOut);
  return ended;
};

// Removes the shared nonce once its session is over, unless a later sign-in has already replaced it
export const releaseSharedSessionNonce = (nonce: string): void => {
  try {
    if (window.localStorage.getItem(SESSION_SCOPE_STORAGE_KEY) === nonce) {
      window.localStorage.removeItem(SESSION_SCOPE_STORAGE_KEY);
    }
  } catch {
    // a nonce we cannot remove is one no other tab could have read either
  }
};

// Another tab signed in or out. The token this tab holds may now belong to an ended session, or the
// cookie to someone else, so its private work stops here; the next refresh decides whether it binds
// again or signs out.
export const noteSharedSessionNonce = (nonce: string | null): void => {
  if (!current || current.nonce === nonce) return;
  const ended = current;
  generation += 1;
  current = null;
  unbound = true;
  announceEnd(ended, false);
};
